@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition
import com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor
import com.taocent.simple.compose.component.richtext.core.RichTextState

/**
 * BlockState 表格键盘导航相关的扩展函数。
 *
 * - [navSelectTable] / [navSelectTableAfter] / [navClearSelection] / [navRestoreIfPending] — 锚点选中态
 *   - [navSelectTable] 写入 [AnchorPosition.Before](视觉左高亮)
 *   - [navSelectTableAfter] 写入 [AnchorPosition.After](视觉右高亮)
 * - [navGoUp] / [navGoDown] — 上下方向键在表格间穿梭(沿用当前 [AnchorPosition])
 * - [navInsertBeforeTable] — 锚点下回车:在上方插入/扩展文本块
 * - [navDeleteFromAbove] — 锚点下 Backspace:删除上方块/字符(含超链接两步删除)
 * - [findNearestTextBlockId] / [focusNearestTextBlock] — 邻近文本块搜索
 * - [navigateFromTable] / [trySelectTableAbove] — 表格/文本块间跳转
 * - [exitTableUpward] / [exitTableDownward] — 单元格→相邻 block 跳出表格
 * - [requestEnterTableFromAbove] / [requestEnterTableFromBelow] — TextBlock ↑↓ 直接进入
 *   相邻表格的首行/末行 cell(替代锚点选中态)
 */

fun BlockState.navSelectTable(tableId: String, sourceBlockId: String) {
    setBlockAnchorCursor(tableId, AnchorPosition.Before)
    navSourceBlockId = sourceBlockId
    selectedTableBlockId = null
    // 表格级左高亮(BEFORE):触发滚动到首列,防止列数 > maxColumnsBeforeScroll 时
    // 用户自己滑动后锚点高亮线被遮挡。TableRenderer 在下一帧消费并清空。
    pendingTableScrollRequest = PendingTableScrollRequest(
        tableId = tableId,
        scrollToStart = true
    )
}

/**
 * 等同于 [navSelectTable],但写入 [AnchorPosition.After] —— 视觉上为表格 **右侧** 高亮线。
 *
 * 触发场景:文本块在 **首位置** 按 Shift+← 且上方为 [TableBlock] 时使用,语义是
 * "从这条线左侧的文本来源跨过去抓住上表的右边界"。后续锚点态下的 ↑↓/Enter/Backspace
 * 行为与 Before 态完全一致(不区分位置)。
 */
fun BlockState.navSelectTableAfter(tableId: String, sourceBlockId: String) {
    setBlockAnchorCursor(tableId, AnchorPosition.After)
    navSourceBlockId = sourceBlockId
    selectedTableBlockId = null
    // 表格级右高亮(AFTER):触发滚动到末列,同上避免锚点高亮线被遮挡。
    pendingTableScrollRequest = PendingTableScrollRequest(
        tableId = tableId,
        scrollToStart = false
    )
}

/**
 * 锚点态下把当前 nav 选中的表格转为"删除选中保护"态:
 *   - 写入 `selectedTableBlockId = navSelectedTableId`(命中 TableRenderer 的整表外框高亮 +
 *     延时自动清除 = `selectionAutoClearDelayMs`,见 [TableRenderer]);
 *   - 清除 nav 锚点(`navClearSelection`),退出 Before/After 高亮线状态。
 *
 * 调用方场景:右高亮(After)态下用户按 Backspace/Delete,期望"二段式确认删除"——
 * 第一次按只切换到选中保护态(给用户反悔机会),短时间内再按一次 Backspace 才真正删表
 * (命中 [BlockRichTextEditor] 顶层 `selectedTableBlockId != null` 分支调 [deleteSelectedTable])。
 *
 * @return true 当本次成功转换;false 当并不在 nav 锚点态(无表可转)。
 */
fun BlockState.navConvertToSelectedTable(): Boolean {
    val tableId = navSelectedTableId ?: return false
    selectedTableBlockId = tableId
    navClearSelection()
    return true
}

/**
 * 左高亮(Before)态下按 ← 的语义:跳出锚点态,光标落到 **上方相邻 TextBlock 的末行末位**
 * (= `text.length`)。视觉上等价于"从表格左边沿向左跨入上方文本块,停在末行行尾"。
 *
 * 与 `navGoUp` 的区别:`navGoUp` 是"按 ↑ 的行迁移语义" → 上方 TextBlock 末行 **首位**;
 * 本函数是"按 ← 的字符级前一格语义" → 上方 TextBlock 末行 **末位**。
 *
 * - 上方相邻是 [TextBlock]:写 selection = `TextRange(text.length)` → `setTextCursor(..., requestFocus = true)`,
 *   返回 true 消费事件。
 * - 上方相邻不是 TextBlock(另一 TableBlock 或锚点表已是文档首块):返回 false,
 *   交还外层默认行为,避免吞事件造成无响应。
 */
fun BlockState.navGoLeftToPrevText(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index <= 0) return false
    val prev = _blocks[index - 1] as? TextBlock ?: return false
    navClearSelection()
    val state = getBlockState(prev.id)
    val tfv = state.textFieldValue
    state.restoreTextFieldValue(
        TextFieldValue(
            annotatedString = tfv.annotatedString,
            selection = TextRange(tfv.text.length)
        )
    )
    setTextCursor(prev.id, requestFocus = true)
    return true
}

/**
 * 右高亮(After)态下按 → 的语义:跳出锚点态,光标落到 **下方相邻 TextBlock 的首行首位**
 * (= `0`)。视觉上等价于"从表格右边沿向右跨入下方文本块,停在首行行首"。
 *
 * 与 `navGoDown` 的区别:`navGoDown` 是"按 ↓ 的行迁移语义" → After 态会落到下方第一行 **末位**
 * (`indexOf('\n')` 之前);本函数是"按 → 的字符级下一格语义" → 一律落 **0**(首行首位)。
 */
fun BlockState.navGoRightToNextText(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index < 0 || index >= _blocks.size - 1) return false
    val next = _blocks[index + 1] as? TextBlock ?: return false
    navClearSelection()
    val state = getBlockState(next.id)
    val tfv = state.textFieldValue
    state.restoreTextFieldValue(
        TextFieldValue(
            annotatedString = tfv.annotatedString,
            selection = TextRange(0)
        )
    )
    setTextCursor(next.id, requestFocus = true)
    return true
}

/**
 * 同一表格内切换 Before / After(左 ↔ 右高亮)。`navSourceBlockId` 保留不动,
 * 因为来源块还是那个,仅高亮线在表格的左右两侧切换。
 *
 * @return true 当本次成功切换;false 当并不在 nav 锚点态。
 */
fun BlockState.navToggleAnchorPosition(): Boolean {
    val anchor = (editorCursor as? com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.BlockAnchor)
        ?: return false
    val tableId = navSelectedTableId ?: return false
    if (anchor.blockId != tableId) return false
    val newPosition = if (anchor.position == AnchorPosition.After) AnchorPosition.Before else AnchorPosition.After
    setBlockAnchorCursor(tableId, newPosition)
    // 同表左↔右翻面:切到 After(右高亮)时滚到末列,切到 Before(左高亮)时滚到首列,
    // 与 navSelectTable/After 行为一致,避免列数 > maxColumnsBeforeScroll 时翻面后高亮线被遮挡。
    pendingTableScrollRequest = PendingTableScrollRequest(
        tableId = tableId,
        scrollToStart = newPosition == AnchorPosition.Before
    )
    return true
}

/**
 * Before(左高亮)态下按 ←,且 **上方相邻不是 TextBlock** 时的 fallback:跳到
 * 上方相邻 [TableBlock] 的 After(右高亮)态。语义是"光标向左穿越当前表的左边界,
 * 落在上一张表的右边界",形成连续的两表锚点链。
 *
 * 上方不存在(锚点表是文档首块)或上方既不是 TextBlock 也不是 TableBlock:返回 false,
 * 由调用方决定是否消费事件。
 */
fun BlockState.navGoLeftToPrevTableAfter(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index <= 0) return false
    val prev = _blocks[index - 1] as? TableBlock ?: return false
    setBlockAnchorCursor(prev.id, AnchorPosition.After)
    navSourceBlockId = null
    selectedTableBlockId = null
    // 跨表跳到上一表的右高亮(After):触发滚动到末列,同上避免高亮线被遮挡。
    pendingTableScrollRequest = PendingTableScrollRequest(
        tableId = prev.id,
        scrollToStart = false
    )
    return true
}

/**
 * After(右高亮)态下按 →,且 **下方相邻不是 TextBlock** 时的 fallback:跳到
 * 下方相邻 [TableBlock] 的 Before(左高亮)态。与 [navGoLeftToPrevTableAfter] 对称,
 * 形成连续两表锚点链。
 */
fun BlockState.navGoRightToNextTableBefore(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index < 0 || index >= _blocks.size - 1) return false
    val next = _blocks[index + 1] as? TableBlock ?: return false
    setBlockAnchorCursor(next.id, AnchorPosition.Before)
    navSourceBlockId = null
    selectedTableBlockId = null
    // 跨表跳到下一表的左高亮(Before):触发滚动到首列,同上避免高亮线被遮挡。
    pendingTableScrollRequest = PendingTableScrollRequest(
        tableId = next.id,
        scrollToStart = true
    )
    return true
}

/**
 * Before(左高亮)态下 Shift+→:进入当前表格的 **首行首列 cell** 并转移焦点。
 *
 * 光标落 cell 开头(`caretAtEnd = false`),与"从左边界进入表格内容"的方向一致。
 * 真正的 focus 请求由 [TableRenderer] 消费 [pendingEnterTableCell] 后调用 `focusCellAtCaret` 完成。
 */
fun BlockState.navEnterFirstCellFromBefore(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val firstRow = table.rows.firstOrNull() ?: return false
    val firstCell = firstRow.cells.firstOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = table.id,
        rowId = firstRow.rowId,
        cellId = firstCell.cellId,
        caretAtEnd = false
    )
    navClearSelection()
    selectedTableBlockId = null
    return true
}

/**
 * After(右高亮)态下 Shift+←:进入当前表格的 **末行末列 cell** 并转移焦点。
 *
 * 光标落 cell 末尾(`caretAtEnd = true`),与"从右边界进入表格内容"的方向一致。
 */
fun BlockState.navEnterLastCellFromAfter(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val lastRow = table.rows.lastOrNull() ?: return false
    val lastCell = lastRow.cells.lastOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = table.id,
        rowId = lastRow.rowId,
        cellId = lastCell.cellId,
        caretAtEnd = true
    )
    navClearSelection()
    selectedTableBlockId = null
    return true
}

/**
 * 行锚点态:行左高亮
 */
fun BlockState.navSelectRowBefore(tableId: String, rowId: String) {
    navRowAnchorTableId = tableId
    navRowAnchorRowId = rowId
    editorCursor = EditorCursor.RowAnchor(tableId, rowId, AnchorPosition.Before)
    navSelectedTableId = null
    navSourceBlockId = null
    selectedTableBlockId = null
    navRowDeleteGuardRowId = null
    // 行级左高亮(BEFORE):触发滚动到首列,防止列数 > maxColumnsBeforeScroll 时
    // 用户自己滑动后行左高亮线被遮挡。
    pendingTableScrollRequest = PendingTableScrollRequest(
        tableId = tableId,
        scrollToStart = true
    )
}

/**
 * 行锚点态:行右高亮
 */
fun BlockState.navSelectRowAfter(tableId: String, rowId: String) {
    navRowAnchorTableId = tableId
    navRowAnchorRowId = rowId
    editorCursor = EditorCursor.RowAnchor(tableId, rowId, AnchorPosition.After)
    navSelectedTableId = null
    navSourceBlockId = null
    selectedTableBlockId = null
    navRowDeleteGuardRowId = null
    // 行级右高亮(AFTER):触发滚动到末列,同上避免行右高亮线被遮挡。
    pendingTableScrollRequest = PendingTableScrollRequest(
        tableId = tableId,
        scrollToStart = false
    )
}

fun BlockState.navClearRowAnchor() {
    navRowAnchorTableId = null
    navRowAnchorRowId = null
    navRowDeleteGuardRowId = null
    if (editorCursor is EditorCursor.RowAnchor) {
        editorCursor = EditorCursor.Text(focusedBlockId)
    }
}

/**
 * 行锚点态 ↑:移到上一行同侧锚点。已是首行时,跳到上方相邻 TextBlock(同表格块锚点逻辑)
 * 或上方 TableBlock(进入其末行的同侧锚点)。
 */
fun BlockState.navRowGoUp(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val position = (editorCursor as? EditorCursor.RowAnchor)?.position ?: AnchorPosition.Before
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex < 0) return false
    if (rowIndex > 0) {
        val prevRow = table.rows[rowIndex - 1]
        if (position == AnchorPosition.After) navSelectRowAfter(tableId, prevRow.rowId)
        else navSelectRowBefore(tableId, prevRow.rowId)
        return true
    }
    // 首行:退出到上方相邻 block
    val tableIndex = findBlockIndex(tableId)
    if (tableIndex <= 0) return false
    val prev = _blocks[tableIndex - 1]
    if (prev is TextBlock) {
        navClearRowAnchor()
        val state = getBlockState(prev.id)
        val tfv = state.textFieldValue
        val targetOffset = if (position == AnchorPosition.After) tfv.text.length
        else tfv.text.lastIndexOf('\n').let { if (it < 0) 0 else it + 1 }
        state.restoreTextFieldValue(
            TextFieldValue(annotatedString = tfv.annotatedString, selection = TextRange(targetOffset))
        )
        setTextCursor(prev.id, requestFocus = true)
        return true
    }
    if (prev is TableBlock) {
        val prevTable = prev
        val lastRow = prevTable.rows.lastOrNull() ?: return false
        navClearRowAnchor()
        if (position == AnchorPosition.After) navSelectRowAfter(prevTable.id, lastRow.rowId)
        else navSelectRowBefore(prevTable.id, lastRow.rowId)
        return true
    }
    return false
}

/**
 * 行锚点态 ↓:移到下一行同侧锚点。已是末行时,跳到下方相邻 TextBlock 或 TableBlock。
 */
fun BlockState.navRowGoDown(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val position = (editorCursor as? EditorCursor.RowAnchor)?.position ?: AnchorPosition.Before
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex < 0) return false
    if (rowIndex < table.rows.lastIndex) {
        val nextRow = table.rows[rowIndex + 1]
        if (position == AnchorPosition.After) navSelectRowAfter(tableId, nextRow.rowId)
        else navSelectRowBefore(tableId, nextRow.rowId)
        return true
    }
    // 末行:退出到下方相邻 block
    val tableIndex = findBlockIndex(tableId)
    if (tableIndex < 0 || tableIndex >= _blocks.size - 1) return false
    val next = _blocks[tableIndex + 1]
    if (next is TextBlock) {
        navClearRowAnchor()
        val state = getBlockState(next.id)
        val tfv = state.textFieldValue
        val targetOffset = if (position == AnchorPosition.After) {
            tfv.text.indexOf('\n').let { if (it < 0) tfv.text.length else it }
        } else 0
        state.restoreTextFieldValue(
            TextFieldValue(annotatedString = tfv.annotatedString, selection = TextRange(targetOffset))
        )
        setTextCursor(next.id, requestFocus = true)
        return true
    }
    if (next is TableBlock) {
        val nextTable = next
        val firstRow = nextTable.rows.firstOrNull() ?: return false
        navClearRowAnchor()
        if (position == AnchorPosition.After) navSelectRowAfter(nextTable.id, firstRow.rowId)
        else navSelectRowBefore(nextTable.id, firstRow.rowId)
        return true
    }
    return false
}

/**
 * 行锚点态 ←:
 * - After(行右高亮)+ ←:同行翻面到 Before
 * - Before(行左高亮)+ ←:
 *   1. 本表上方还有行 → 上方行的行右(After)
 *   2. 上方是 TableBlock → 该表尾行的行右(After)
 *   3. 上方是 TextBlock → 末行末位
 *   4. 都没有 → 不消费
 */
fun BlockState.navRowGoLeft(): Boolean {
    val position = (editorCursor as? EditorCursor.RowAnchor)?.position ?: return false
    if (position == AnchorPosition.After) {
        val tableId = navRowAnchorTableId ?: return false
        val rowId = navRowAnchorRowId ?: return false
        navSelectRowBefore(tableId, rowId)
        return true
    }
    // Before:优先本表上一行
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex > 0) {
        navSelectRowAfter(tableId, table.rows[rowIndex - 1].rowId)
        return true
    }
    // 首行:退出到上方相邻 block
    val tableIndex = findBlockIndex(tableId)
    if (tableIndex <= 0) return false
    val prev = _blocks[tableIndex - 1]
    if (prev is TableBlock) {
        val lastRow = prev.rows.lastOrNull() ?: return false
        navClearRowAnchor()
        navSelectRowAfter(prev.id, lastRow.rowId)
        return true
    }
    if (prev is TextBlock) {
        navClearRowAnchor()
        val state = getBlockState(prev.id)
        val tfv = state.textFieldValue
        state.restoreTextFieldValue(
            TextFieldValue(annotatedString = tfv.annotatedString, selection = TextRange(tfv.text.length))
        )
        setTextCursor(prev.id, requestFocus = true)
        return true
    }
    return false
}

/**
 * 行锚点态 →:
 * - Before(行左高亮)+ →:同行翻面到 After
 * - After(行右高亮)+ →:
 *   1. 本表下方还有行 → 下方行的行左(Before)
 *   2. 下方是 TableBlock → 该表首行的行左(Before)
 *   3. 下方是 TextBlock → 首行首位
 *   4. 都没有 → 不消费
 */
fun BlockState.navRowGoRight(): Boolean {
    val position = (editorCursor as? EditorCursor.RowAnchor)?.position ?: return false
    if (position == AnchorPosition.Before) {
        val tableId = navRowAnchorTableId ?: return false
        val rowId = navRowAnchorRowId ?: return false
        navSelectRowAfter(tableId, rowId)
        return true
    }
    // After:优先本表下一行
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex < table.rows.lastIndex) {
        navSelectRowBefore(tableId, table.rows[rowIndex + 1].rowId)
        return true
    }
    // 末行:退出到下方相邻 block
    val tableIndex = findBlockIndex(tableId)
    if (tableIndex < 0 || tableIndex >= _blocks.size - 1) return false
    val next = _blocks[tableIndex + 1]
    if (next is TableBlock) {
        val firstRow = next.rows.firstOrNull() ?: return false
        navClearRowAnchor()
        navSelectRowBefore(next.id, firstRow.rowId)
        return true
    }
    if (next is TextBlock) {
        navClearRowAnchor()
        val state = getBlockState(next.id)
        val tfv = state.textFieldValue
        state.restoreTextFieldValue(
            TextFieldValue(annotatedString = tfv.annotatedString, selection = TextRange(0))
        )
        setTextCursor(next.id, requestFocus = true)
        return true
    }
    return false
}

/**
 * 行锚点态 Shift+←:After(行右高亮)穿透进入当前行末列 cell
 */
fun BlockState.navRowEnterLastCell(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val row = table.rows.firstOrNull { it.rowId == rowId } ?: return false
    val lastCell = row.cells.lastOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = tableId, rowId = rowId, cellId = lastCell.cellId, caretAtEnd = true
    )
    navClearRowAnchor()
    selectedTableBlockId = null
    return true
}

/**
 * 行锚点态 Shift+→:Before(行左高亮)穿透进入当前行首列 cell
 */
fun BlockState.navRowEnterFirstCell(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val row = table.rows.firstOrNull { it.rowId == rowId } ?: return false
    val firstCell = row.cells.firstOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = tableId, rowId = rowId, cellId = firstCell.cellId, caretAtEnd = false
    )
    navClearRowAnchor()
    selectedTableBlockId = null
    return true
}

/**
 * 行锚点态 Enter:Before 在行上方插新行;After 在行下方插新行,焦点转新行
 */
/**
 * 行锚点态按 Enter:拆分/换行键。
 *
 * - Before(行左高亮):
 *   - 首行+上方是 TextBlock:给上方文本块末尾加 `\n`。
 *   - 首行+上方不是 TextBlock:新建空 [TextBlock] 插入到表格上方。
 *   - 非首行:拆分表格——上方行一个表格,当前行+下方行另一个表格,
 *     焦点落到下方新表首行首格,保持行左高亮态。
 * - After(行右高亮):
 *   - 末行+下方是 TextBlock:给下方文本块首位插入 `\n`。
 *   - 末行+下方不是 TextBlock:新建空 [TextBlock] 插入到表格下方。
 *   - 非末行:拆分表格——上方行+当前行一个表格,下方行另一个表格,
 *     焦点落到下方新表首行首格,保持行右高亮态。
 */
fun BlockState.navRowSplitOrNewline(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val tableIndex = findBlockIndex(tableId)
    if (tableIndex < 0) return false
    val table = _blocks[tableIndex] as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex < 0) return false
    val position = (editorCursor as? EditorCursor.RowAnchor)?.position ?: AnchorPosition.Before
    val before = documentSnapshot()

    // ===== Before(行左高亮) =====
    if (position == AnchorPosition.Before) {
        if (rowIndex == 0) {
            if (tableIndex > 0) {
                val prevBlock = _blocks[tableIndex - 1]
                if (prevBlock is TextBlock) {
                    val state = getBlockState(prevBlock.id)
                    val currentText = state.textFieldValue.text
                    state.restoreTextFieldValue(
                        TextFieldValue(text = currentText + "\n", selection = TextRange(currentText.length + 1))
                    )
                    setTextCursor(prevBlock.id, requestFocus = true)
                    navClearRowAnchor()
                    recordDocumentChange(before)
                    return true
                }
            }
            val newBlock = TextBlock()
            _blocks.add(tableIndex, newBlock)
            blockStates[newBlock.id] = createBlockState()
            setTextCursor(newBlock.id, requestFocus = true)
            navClearRowAnchor()
            recordDocumentChange(before)
            return true
        }
        // 非首行:拆分——上方行一个表,当前行+下方行另一个表
        val upperRows = table.rows.subList(0, rowIndex)
        val lowerRows = table.rows.subList(rowIndex, table.rows.size)
        if (upperRows.isEmpty() || lowerRows.isEmpty()) return false
        val upperTable = TableBlock(id = generateBlockId(), rows = upperRows.map { it.copy() })
        val lowerTable = table.copy(rows = lowerRows.map { it.copy() })
        _blocks[tableIndex] = upperTable
        _blocks.add(tableIndex + 1, lowerTable)
        blockStates.remove(tableId)
        val firstRowLower = lowerTable.rows.firstOrNull() ?: return false
        val firstCell = firstRowLower.cells.firstOrNull() ?: return false
        pendingEnterTableCell = PendingEnterTableCell(
            tableId = lowerTable.id, rowId = firstRowLower.rowId, cellId = firstCell.cellId, caretAtEnd = false
        )
        navSelectRowBefore(lowerTable.id, firstRowLower.rowId)
        recordDocumentChange(before)
        return true
    }

    // ===== After(行右高亮) =====
    if (rowIndex == table.rows.lastIndex) {
        val nextIndex = tableIndex + 1
        if (nextIndex < _blocks.size) {
            val nextBlock = _blocks[nextIndex]
            if (nextBlock is TextBlock) {
                val state = getBlockState(nextBlock.id)
                val currentText = state.textFieldValue.text
                state.restoreTextFieldValue(
                    TextFieldValue(text = "\n" + currentText, selection = TextRange(1))
                )
                setTextCursor(nextBlock.id, requestFocus = true)
                navClearRowAnchor()
                recordDocumentChange(before)
                return true
            }
        }
        val newBlock = TextBlock()
        _blocks.add(nextIndex, newBlock)
        blockStates[newBlock.id] = createBlockState()
        setTextCursor(newBlock.id, requestFocus = true)
        navClearRowAnchor()
        recordDocumentChange(before)
        return true
    }
    // 非末行:拆分——上方行+当前行一个表,下方行另一个表
    val upperRows = table.rows.subList(0, rowIndex + 1)
    val lowerRows = table.rows.subList(rowIndex + 1, table.rows.size)
    if (upperRows.isEmpty() || lowerRows.isEmpty()) return false
    val upperTable = table.copy(rows = upperRows.map { it.copy() })
    val lowerTable = TableBlock(id = generateBlockId(), rows = lowerRows.map { it.copy() })
    _blocks[tableIndex] = upperTable
    _blocks.add(tableIndex + 1, lowerTable)
    blockStates.remove(tableId)
    val firstRowLower = lowerTable.rows.firstOrNull() ?: return false
    val firstCell = firstRowLower.cells.firstOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = lowerTable.id, rowId = firstRowLower.rowId, cellId = firstCell.cellId, caretAtEnd = false
    )
    navSelectRowAfter(lowerTable.id, firstRowLower.rowId)
    recordDocumentChange(before)
    return true
}

/**
 * 行左高亮(Before)态下按 Tab:在当前行上方插入新行。
 */
fun BlockState.navRowInsertRowAbove(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex < 0) return false
    val before = documentSnapshot()
    insertTableRow(tableId, afterRowIndex = rowIndex, insertAbove = true)
    val newTable = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val newRow = newTable.rows.getOrNull(rowIndex) ?: return false
    val targetCell = newRow.cells.firstOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = tableId, rowId = newRow.rowId, cellId = targetCell.cellId, caretAtEnd = false
    )
    navClearRowAnchor()
    recordDocumentChange(before)
    return true
}

/**
 * 行右高亮(After)态下按 Tab:在当前行下方插入新行。
 */
fun BlockState.navRowInsertRowBelow(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex < 0) return false
    val before = documentSnapshot()
    insertTableRow(tableId, afterRowIndex = rowIndex, insertAbove = false)
    val newTable = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val newRow = newTable.rows.getOrNull(rowIndex + 1) ?: return false
    val targetCell = newRow.cells.firstOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = tableId, rowId = newRow.rowId, cellId = targetCell.cellId, caretAtEnd = false
    )
    navClearRowAnchor()
    recordDocumentChange(before)
    return true
}

/**
 * 行锚点态 Backspace/Delete:
 *
 * - Before(行左高亮)+ Backspace:
 *   - 非首行:高亮上方行(二段式:第一次整行外框高亮+自动取消,第二次删上方行)。
 *   - 首行+上方 TextBlock:删末尾字符(含超链接两段删除,与表格左高亮 `navDeleteFromAbove` 一致),
 *     保持行左高亮态。
 *   - 首行+上方 TableBlock:删上表末行(二段式,复用 `selectedTableBlockId`)。
 *   - 首行+无上方块:二段式删本行。
 * - After(行右高亮)+ Backspace/Delete:
 *   - 总是删除**本行**(高亮本行,第二次删本行/本表),无论下方是否有行或文本块。
 */
fun BlockState.navRowDeleteRow(): Boolean {
    val tableId = navRowAnchorTableId ?: return false
    val rowId = navRowAnchorRowId ?: return false
    val tableIndex = findBlockIndex(tableId)
    if (tableIndex < 0) return false
    val table = _blocks[tableIndex] as? TableBlock ?: return false
    val rowIndex = table.rows.indexOfFirst { it.rowId == rowId }
    if (rowIndex < 0) return false
    val position = (editorCursor as? EditorCursor.RowAnchor)?.position ?: AnchorPosition.Before
    val before = documentSnapshot()

    // ===== After(行右高亮):总是删除本行(高亮本行) =====
    if (position == AnchorPosition.After) {
        if (navRowDeleteGuardRowId != rowId) {
            navRowDeleteGuardRowId = rowId
            navRowDeleteGuardTime = 0L
            recordDocumentChange(before)
            return true
        }
        val wasFirstRow = rowIndex == 0
        if (table.rows.size == 1) {
            deleteTable(tableId)
        } else {
            deleteTableRow(tableId, rowIndex)
        }
        navRowDeleteGuardRowId = null
        navRowDeleteGuardTime = null
        recordDocumentChange(before)
        navClearRowAnchor()

        // 焦点转移逻辑
        if (wasFirstRow) {
            // 首行被删除:焦点转移到上方相邻块
            val tableIdx = findBlockIndex(tableId)
            if (tableIdx > 0) {
                val prevBlock = _blocks[tableIdx - 1]
                when (prevBlock) {
                    is TextBlock -> {
                        val state = getBlockState(prevBlock.id)
                        val content = state.textFieldValue.annotatedString
                        if (content.isNotEmpty()) {
                            state.restoreTextFieldValue(
                                TextFieldValue(
                                    annotatedString = content,
                                    selection = TextRange(content.length)
                                )
                            )
                            syncBlockContent(prevBlock.id)
                            setTextCursor(prevBlock.id, requestFocus = true)
                        } else {
                            setTextCursor(prevBlock.id, requestFocus = true)
                        }
                    }
                    is TableBlock -> {
                        // 转移到上方表格的末行末格
                        val targetTable = prevBlock
                        val lastRow = targetTable.rows.last()
                        val lastCell = lastRow.cells.lastOrNull()
                        if (lastCell != null) {
                            pendingEnterTableCell = PendingEnterTableCell(
                                tableId = targetTable.id,
                                rowId = lastRow.rowId,
                                cellId = lastCell.cellId,
                                caretAtEnd = true
                            )
                        }
                        setFocusedBlockCompat(targetTable.id, requestFocus = true)
                    }
                }
            }
        } else {
            // 非首行被删除:焦点转移到上方行末格
            val newRowIdx = (rowIndex - 1).coerceAtLeast(0)
            val currentTable = _blocks.firstOrNull { it.id == tableId } as? TableBlock
            if (currentTable != null && currentTable.rows.isNotEmpty()) {
                val newRow = currentTable.rows[newRowIdx.coerceAtMost(currentTable.rows.lastIndex)]
                val lastCell = newRow.cells.lastOrNull()
                if (lastCell != null) {
                    pendingEnterTableCell = PendingEnterTableCell(
                        tableId = currentTable.id,
                        rowId = newRow.rowId,
                        cellId = lastCell.cellId,
                        caretAtEnd = true
                    )
                }
            }
        }
        return true
    }

    // ===== Before(行左高亮):删上方 =====
    // 非首行:高亮上方行(二段式)
    if (rowIndex > 0) {
        val prevRowId = table.rows[rowIndex - 1].rowId
        if (navRowDeleteGuardRowId != prevRowId) {
            navRowDeleteGuardRowId = prevRowId
            navRowDeleteGuardTime = 0L
            recordDocumentChange(before)
            return true
        }
        deleteTableRow(tableId, rowIndex - 1)
        navRowDeleteGuardRowId = null
        navRowDeleteGuardTime = null
        val newTable = _blocks.firstOrNull { it.id == tableId } as? TableBlock
        if (newTable != null && newTable.rows.isNotEmpty()) {
            val newRow = newTable.rows[(rowIndex - 1).coerceAtMost(newTable.rows.lastIndex).coerceAtLeast(0)]
            val firstCell = newRow.cells.firstOrNull()
            if (firstCell != null) {
                pendingEnterTableCell = PendingEnterTableCell(
                    tableId = tableId, rowId = newRow.rowId, cellId = firstCell.cellId, caretAtEnd = false
                )
            }
        }
        recordDocumentChange(before)
        navClearRowAnchor()
        return true
    }

    // 首行+上方有相邻 block
    if (tableIndex > 0) {
        val targetIndex = tableIndex - 1
        val targetBlock = _blocks[targetIndex]

        // 上方是 TextBlock:像表格左高亮那样删末尾字符(含超链接两段删除)
        if (targetBlock is TextBlock) {
            val state = getBlockState(targetBlock.id)
            val content = state.textFieldValue.annotatedString
            if (content.isEmpty()) {
                _blocks.removeAt(targetIndex)
                blockStates.remove(targetBlock.id)
                recordDocumentChange(before)
                return true
            }
            val deleteStart = content.length - 1
            val links = content.getStringAnnotations(RichTextState.HYPERLINK_TAG, deleteStart, content.length)
            if (links.isNotEmpty()) {
                val link = links.first()
                val hyperlinkDeleteKey = "${targetBlock.id}:${link.start}:${link.end}:${link.item}"
                if (pendingHyperlinkDeleteKey == hyperlinkDeleteKey) {
                    val snapshot = pendingHyperlinkDeleteSnapshot ?: before
                    pendingHyperlinkDeleteSnapshot = null
                    pendingHyperlinkDeleteKey = null
                    val newContent = content.subSequence(0, link.start) + content.subSequence(link.end, content.length)
                    state.restoreTextFieldValue(
                        TextFieldValue(annotatedString = newContent, selection = TextRange(link.start))
                    )
                    this.syncBlockContent(targetBlock.id)
                    recordDocumentChange(snapshot)
                } else {
                    state.restoreTextFieldValue(
                        state.textFieldValue.copy(selection = TextRange(link.start, link.end))
                    )
                    pendingHyperlinkDeleteSnapshot = before
                    pendingHyperlinkDeleteKey = hyperlinkDeleteKey
                    recordDocumentChange(before)
                }
                return true
            }
            val newContent = content.subSequence(0, deleteStart)
            state.restoreTextFieldValue(
                TextFieldValue(annotatedString = newContent, selection = TextRange(deleteStart))
            )
            this.syncBlockContent(targetBlock.id)
            recordDocumentChange(before)
            return true
        }

        // 上方是 TableBlock:删上表末行(二段式,复用 selectedTableBlockId)
        if (targetBlock is TableBlock) {
            if (selectedTableBlockId != targetBlock.id) {
                selectedTableBlockId = targetBlock.id
                recordDocumentChange(before)
                return true
            }
            if (targetBlock.rows.size == 1) {
                deleteTable(targetBlock.id)
            } else {
                deleteTableRow(targetBlock.id, targetBlock.rows.lastIndex)
            }
            selectedTableBlockId = null
            recordDocumentChange(before)
            navClearRowAnchor()
            return true
        }
    }

    // 首行+无上方块:二段式删本行
    if (navRowDeleteGuardRowId != rowId) {
        navRowDeleteGuardRowId = rowId
        navRowDeleteGuardTime = 0L
        recordDocumentChange(before)
        return true
    }
    if (table.rows.size == 1) {
        deleteTable(tableId)
    } else {
        deleteTableRow(tableId, rowIndex)
    }
    navRowDeleteGuardRowId = null
    navRowDeleteGuardTime = null
    recordDocumentChange(before)
    navClearRowAnchor()
    return true
}

fun BlockState.navClearSelection() {
    navSelectedTableId = null
    navSourceBlockId = null
    navRowAnchorTableId = null
    navRowAnchorRowId = null
    navRowDeleteGuardRowId = null
    navRowDeleteGuardTime = null
    val current = editorCursor
    if (current is com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.BlockAnchor
        || current is com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.RowAnchor
    ) {
        editorCursor = com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.Text(focusedBlockId)
    }
}

fun BlockState.navRestoreIfPending() {
    val restoreId = pendingNavRestoreTableId ?: return
    pendingNavRestoreTableId = null
    setBlockAnchorCursor(restoreId)
}

fun BlockState.navGoUp(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index <= 0) return false
    val prevBlock = _blocks[index - 1]
    // 锚点态在表格间穿梭时,沿用当前 AnchorPosition(左/右高亮),
    // 否则会从 After 态切表后被强制重置为 Before,视觉跳变。
    val currentPosition = (editorCursor as? com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.BlockAnchor)
        ?.position
        ?: com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.Before
    if (prevBlock is TableBlock) {
        setBlockAnchorCursor(prevBlock.id, currentPosition)
        navSourceBlockId = null
        return true
    }
    if (prevBlock is TextBlock) {
        navClearSelection()
        // 落点策略:
        //   Before(左高亮)→ 上方 TextBlock 最后一行 **首位**(最后一个 '\n' 之后)
        //   After (右高亮)→ 上方 TextBlock 最后一行 **末位**(text.length)
        val state = getBlockState(prevBlock.id)
        val tfv = state.textFieldValue
        val text = tfv.text
        val targetOffset = if (currentPosition == com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After) {
            text.length
        } else {
            text.lastIndexOf('\n').let { if (it < 0) 0 else it + 1 }
        }
        state.restoreTextFieldValue(
            TextFieldValue(
                annotatedString = tfv.annotatedString,
                selection = TextRange(targetOffset)
            )
        )
        setTextCursor(prevBlock.id, requestFocus = true)
        return true
    }
    return false
}

fun BlockState.navGoDown(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index < 0 || index >= _blocks.size - 1) return false
    val nextBlock = _blocks[index + 1]
    val currentPosition = (editorCursor as? com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor.BlockAnchor)
        ?.position
        ?: com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.Before
    if (nextBlock is TableBlock) {
        setBlockAnchorCursor(nextBlock.id, currentPosition)
        navSourceBlockId = null
        return true
    }
    if (nextBlock is TextBlock) {
        navClearSelection()
        // 落点策略:
        //   Before(左高亮)→ 下方 TextBlock 第一行 **首位**(0)
        //   After (右高亮)→ 下方 TextBlock 第一行 **末位**(第一个 '\n' 之前;无 '\n' 则 text.length)
        val state = getBlockState(nextBlock.id)
        val tfv = state.textFieldValue
        val text = tfv.text
        val targetOffset = if (currentPosition == com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After) {
            text.indexOf('\n').let { if (it < 0) text.length else it }
        } else {
            0
        }
        state.restoreTextFieldValue(
            TextFieldValue(
                annotatedString = tfv.annotatedString,
                selection = TextRange(targetOffset)
            )
        )
        setTextCursor(nextBlock.id, requestFocus = true)
        return true
    }
    return false
}

fun BlockState.navInsertBeforeTable(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val before = documentSnapshot()
    val prevBlock = if (index > 0) _blocks[index - 1] else null
    if (prevBlock is TextBlock) {
        val state = getBlockState(prevBlock.id)
        val currentText = state.textFieldValue.text
        state.restoreTextFieldValue(
            androidx.compose.ui.text.input.TextFieldValue(
                text = currentText + "\n",
                selection = androidx.compose.ui.text.TextRange(currentText.length + 1)
            )
        )
        setTextCursor(prevBlock.id, requestFocus = true)
        navClearSelection()
        recordDocumentChange(before)
        return true
    } else {
        val newBlock = TextBlock()
        _blocks.add(index, newBlock)
        blockStates[newBlock.id] = createBlockState()
        setTextCursor(newBlock.id, requestFocus = true)
        navClearSelection()
        recordDocumentChange(before)
        return true
    }
}

/**
 * 右高亮(After)态下按 Enter:
 * - 下方相邻是 [TextBlock]:在该块**开头**插入一个 `\n`,光标落到 `pos = 0`,
 *   视觉上表格下方多出一空行,光标停在该空行(即原内容上方一行)。
 *   与 [navInsertBeforeTable] 在 Before 态把 `\n` 加到上方块**末尾**且光标停留在末尾的语义对称。
 * - 下方不是 TextBlock(包括另一个 TableBlock、或锚点表已是文档末块):
 *   新建空 [TextBlock] 插入到 `nextIndex`,焦点转移到新块。
 *
 * 调用方:[BlockRichTextEditor] 顶层 onPreviewKeyEvent 在
 * `editorCursor is BlockAnchor && position == After` 时处理 Enter。
 */
fun BlockState.navInsertAfterTable(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val before = documentSnapshot()
    val nextIndex = index + 1
    val nextBlock = if (nextIndex < _blocks.size) _blocks[nextIndex] else null
    if (nextBlock is TextBlock) {
        val state = getBlockState(nextBlock.id)
        val currentText = state.textFieldValue.text
        state.restoreTextFieldValue(
            androidx.compose.ui.text.input.TextFieldValue(
                text = "\n" + currentText,
                selection = androidx.compose.ui.text.TextRange(0)
            )
        )
        setTextCursor(nextBlock.id, requestFocus = true)
        navClearSelection()
        recordDocumentChange(before)
        return true
    } else {
        val newBlock = TextBlock()
        _blocks.add(nextIndex, newBlock)
        blockStates[newBlock.id] = createBlockState()
        setTextCursor(newBlock.id, requestFocus = true)
        navClearSelection()
        recordDocumentChange(before)
        return true
    }
}

fun BlockState.navDeleteFromAbove(): Boolean {
    val tableId = navSelectedTableId ?: return false
    val index = findBlockIndex(tableId)
    if (index <= 0) return false
    val targetIndex = index - 1
    val prevBlock = _blocks[targetIndex]
    val before = documentSnapshot()
    val deletedFocusedBlock = prevBlock.id == focusedBlockId
    if (prevBlock is TableBlock) {
        if (selectedTableBlockId == prevBlock.id) {
            _blocks.removeAt(targetIndex)
            blockStates.remove(prevBlock.id)
            selectedTableBlockId = null
            recordDocumentChange(before)
        } else {
            selectedTableBlockId = prevBlock.id
            recordDocumentChange(before)
        }
        setBlockAnchorCursor(tableId)
        return true
    }
    if (prevBlock is TextBlock) {
        val state = getBlockState(prevBlock.id)
        val content = state.textFieldValue.annotatedString
        if (content.isNotEmpty()) {
            val deleteStart = content.length - 1
            val links = content.getStringAnnotations(RichTextState.HYPERLINK_TAG, deleteStart, content.length)
            if (links.isNotEmpty()) {
                val link = links.first()
                val hyperlinkDeleteKey = "${prevBlock.id}:${link.start}:${link.end}:${link.item}"
                val selection = state.textFieldValue.selection
                if (selection.min <= link.start && selection.max >= link.end) {
                    val deleteBefore = if (pendingHyperlinkDeleteKey == hyperlinkDeleteKey) {
                        pendingHyperlinkDeleteSnapshot ?: before
                    } else {
                        before
                    }
                    val builder = AnnotatedString.Builder()
                    if (link.start > 0) builder.append(content.subSequence(0, link.start))
                    if (link.end < content.length) builder.append(content.subSequence(link.end, content.length))
                    val newContent = builder.toAnnotatedString()
                    state.restoreTextFieldValue(
                        TextFieldValue(
                            annotatedString = newContent,
                            selection = TextRange(link.start)
                        )
                    )
                    syncBlockContent(prevBlock.id)
                    setBlockAnchorCursor(tableId)
                    if (deletedFocusedBlock) {
                        setTextFocusOnly(prevBlock.id, requestFocus = true)
                    }
                    recordDocumentChange(deleteBefore)
                    return true
                }
                state.restoreTextFieldValue(
                    state.textFieldValue.copy(
                        selection = TextRange(link.start, link.end)
                    )
                )
                setBlockAnchorCursor(tableId)
                if (deletedFocusedBlock) {
                    setTextFocusOnly(prevBlock.id, requestFocus = true)
                }
                pendingHyperlinkDeleteSnapshot = before
                pendingHyperlinkDeleteKey = hyperlinkDeleteKey
                return true
            }
            val newContent = content.subSequence(0, deleteStart)
            state.restoreTextFieldValue(
                TextFieldValue(
                    annotatedString = newContent,
                    selection = TextRange(newContent.length)
                )
            )
            syncBlockContent(prevBlock.id)
            setBlockAnchorCursor(tableId)
            if (deletedFocusedBlock) {
                setTextFocusOnly(prevBlock.id, requestFocus = true)
            }
            recordDocumentChange(before)
            return true
        }
        _blocks.removeAt(targetIndex)
        blockStates.remove(prevBlock.id)
        setBlockAnchorCursor(tableId)
        if (deletedFocusedBlock) {
            restoreNearestTextFocus(targetIndex)
        }
        recordDocumentChange(before)
        return true
    }
    return false
}

fun BlockState.findNearestTextBlockId(anchorIndex: Int): String? {
    val startIndex = anchorIndex.coerceAtLeast(0)
    for (i in startIndex until _blocks.size) {
        val block = _blocks[i]
        if (block is TextBlock) return block.id
    }
    val endIndex = anchorIndex.coerceAtMost(_blocks.lastIndex)
    for (i in endIndex downTo 0) {
        val block = _blocks[i]
        if (block is TextBlock) return block.id
    }
    return null
}

fun BlockState.focusNearestTextBlock(anchorIndex: Int) {
    findNearestTextBlockId(anchorIndex)?.let {
        setTextCursor(it, requestFocus = true)
    }
}

fun BlockState.navigateFromTable(tableId: String, direction: Int): Boolean {
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= _blocks.size) return false
    val targetBlock = _blocks[targetIndex]
    if (targetBlock is TextBlock) {
        navClearSelection()
        setFocusedBlockCompat(targetBlock.id)
        return true
    }
    if (targetBlock is TableBlock) {
        setBlockAnchorCursor(targetBlock.id)
        navSourceBlockId = null
        return true
    }
    return false
}

fun BlockState.trySelectTableAbove(blockId: String): Boolean {
    val index = findBlockIndex(blockId)
    if (index <= 0) return false
    val previousBlock = _blocks[index - 1]
    if (previousBlock is TableBlock) {
        selectedTableBlockId = previousBlock.id
        return true
    }
    return false
}

/**
 * 从表格单元格"向上"跳出到相邻 block。
 *
 * - 上方是 [TextBlock]:清掉 cell 焦点,光标落在 TextBlock **末尾**,焦点切到该块
 * - 上方是 [TableBlock]:
 *   - [useAnchor] = true(Shift+↑ 触发):走锚点选中态,整表外框高亮
 *   - [useAnchor] = false(默认 ↑ 触发):写 [pendingEnterTableCell] 直接进入上表的末行 cell,
 *     与 TextBlock → Table 的 Plan B 行为对齐(参见 [requestEnterTableFromAbove])
 * - 表格已是文档首块:返回 false,事件不消费
 *
 * 调用方:[com.taocent.simple.compose.component.blockrichtext.internal.block.renderer.TableRenderer]
 * 在单元格首行按 ↑ 或 (row=0, col=0) 按 ← 时使用。
 */
internal fun BlockState.exitTableUpward(
    tableId: String,
    useAnchor: Boolean = false,
    preferredColumn: Int = 0
): Boolean {
    val index = findBlockIndex(tableId)
    if (index <= 0) return false
    val prev = _blocks[index - 1]
    if (prev is TextBlock) {
        clearTableCellFocus()
        val state = getBlockState(prev.id)
        val tfv = state.textFieldValue
        state.restoreTextFieldValue(
            TextFieldValue(
                annotatedString = tfv.annotatedString,
                selection = TextRange(tfv.text.length)
            )
        )
        setTextCursor(prev.id, requestFocus = true)
        return true
    }
    if (prev is TableBlock) {
        clearTableCellFocus()
        return if (useAnchor) {
            navSelectTable(prev.id, tableId)
            true
        } else {
            requestEnterTableFromAbove(prev.id, preferredColumn)
        }
    }
    return false
}

/**
 * 从表格单元格"向下"跳出到相邻 block。
 *
 * - 下方是 [TextBlock]:清掉 cell 焦点,光标落在 TextBlock **开头**,焦点切到该块
 * - 下方是 [TableBlock]:
 *   - [useAnchor] = true(Shift+↓ 触发):走锚点选中态
 *   - [useAnchor] = false(默认 ↓ 触发):写 [pendingEnterTableCell] 直接进入下表的首行 cell
 * - 表格是文档末块且 [autoCreate] 为 true:**自动新建空 [TextBlock]** 追加到文档末尾,
 *   并把焦点切到该新块(与 [navInsertBeforeTable] 形成对称)
 * - 表格是文档末块且 [autoCreate] 为 false:返回 false,事件不消费
 *
 * 调用方:[com.taocent.simple.compose.component.blockrichtext.internal.block.renderer.TableRenderer]
 * 在单元格末行按 ↓ 或 (row=last, col=last) 按 → 时使用。
 */
internal fun BlockState.exitTableDownward(
    tableId: String,
    autoCreate: Boolean = true,
    useAnchor: Boolean = false,
    preferredColumn: Int = 0
): Boolean {
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val nextIndex = index + 1
    if (nextIndex >= _blocks.size) {
        if (!autoCreate) return false
        val before = documentSnapshot()
        clearTableCellFocus()
        val newBlock = TextBlock()
        _blocks.add(newBlock)
        blockStates[newBlock.id] = createBlockState()
        setTextCursor(newBlock.id, requestFocus = true)
        recordDocumentChange(before)
        return true
    }
    val next = _blocks[nextIndex]
    if (next is TextBlock) {
        clearTableCellFocus()
        val state = getBlockState(next.id)
        val tfv = state.textFieldValue
        state.restoreTextFieldValue(
            TextFieldValue(
                annotatedString = tfv.annotatedString,
                selection = TextRange(0)
            )
        )
        setTextCursor(next.id, requestFocus = true)
        return true
    }
    if (next is TableBlock) {
        clearTableCellFocus()
        return if (useAnchor) {
            navSelectTable(next.id, tableId)
            true
        } else {
            requestEnterTableFromBelow(next.id, preferredColumn)
        }
    }
    return false
}

/**
 * TextBlock 块首按 ↑ 时,如果上方是 [TableBlock],**直接**进入该表格的末行 cell。
 *
 * 决策:列(column)使用 [preferredColumn](默认 0),映射到 `lastRow.cells` 时取
 * `coerceAtMost(lastRow.cells.lastIndex)`,以兼容不规则表格(理论上当前数据模型严格 m×n,
 * 但保留鲁棒性)。光标落在该 cell **末尾**。
 *
 * 写入路径:仅写 [pendingEnterTableCell]。
 * [com.taocent.simple.compose.component.blockrichtext.internal.block.renderer.TableRenderer]
 * 在下一次组合中通过 LaunchedEffect 监听 + focusCellAtCaret + 清空请求,
 * 之后 cell 的 onFocusChanged 会自然驱动 [setTableCellCursor] 更新 editorCursor / focusedTableCellState 等。
 *
 * 不在此函数同步切 editorCursor,是因为目标 cell 的 RichTextState/FocusRequester remember
 * 在 TableRenderer 内部,**此处无法直接获取**。强行先切 cursor 反而会让 toolbar 等订阅者
 * 看到不完整的中间态。
 *
 * @return true 表示请求成功(上方确为 TableBlock 且非空表格),调用方应消费 KeyEvent。
 *         false 表示上方不是表格、或目标表格无行/无 cell,调用方继续走默认/锚点回退。
 */
internal fun BlockState.requestEnterTableFromAbove(tableId: String, preferredColumn: Int = 0): Boolean {
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val lastRow = table.rows.lastOrNull() ?: return false
    if (lastRow.cells.isEmpty()) return false
    val targetCol = preferredColumn.coerceIn(0, lastRow.cells.lastIndex)
    val targetCell = lastRow.cells[targetCol]
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = tableId,
        rowId = lastRow.rowId,
        cellId = targetCell.cellId,
        caretAtEnd = true
    )
    return true
}

/**
 * TextBlock 块末按 ↓ 时,如果下方是 [TableBlock],**直接**进入该表格的首行 cell。
 *
 * 与 [requestEnterTableFromAbove] 对称:目标行 = 首行;光标落在 cell **开头**。
 */
internal fun BlockState.requestEnterTableFromBelow(tableId: String, preferredColumn: Int = 0): Boolean {
    val table = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val firstRow = table.rows.firstOrNull() ?: return false
    if (firstRow.cells.isEmpty()) return false
    val targetCol = preferredColumn.coerceIn(0, firstRow.cells.lastIndex)
    val targetCell = firstRow.cells[targetCol]
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = tableId,
        rowId = firstRow.rowId,
        cellId = targetCell.cellId,
        caretAtEnd = false
    )
    return true
}

/**
 * 表格首行首列 (0, 0) 在 cell **首位置**按 ← 时的"前一格"跨 block 跳出。
 *
 * 与 [exitTableUpward] 的关键区别:
 * - [exitTableUpward](默认 ↑ 触发):**行迁移**语义,跨表时落点 = 上表末行 **同列**
 *   (按 [preferredColumn] 列号记忆),让 ↑ 在视觉上"恰好上移一行"。
 * - 本函数(← 触发):**前一格**语义,跨表时落点 = 上表 **末行末列**(尾格),
 *   与电子表格里 ← 在 (0,0) 等价于"跳到上一个连续编辑位"对齐,也与
 *   [shiftTabExitTable] 在 (0,0) 的语义保持一致。
 *
 * 上方是 [TextBlock]:光标落到该块**末尾**(沿用 [exitTableUpward] 同分支语义)。
 * 表格已是文档首块:返回 false,事件不消费。
 */
internal fun BlockState.exitTableLeftToPrevCell(tableId: String): Boolean {
    val index = findBlockIndex(tableId)
    if (index <= 0) return false
    val prev = _blocks[index - 1]
    if (prev is TableBlock) {
        clearTableCellFocus()
        val lastRow = prev.rows.lastOrNull() ?: return false
        val targetCell = lastRow.cells.lastOrNull() ?: return false
        pendingEnterTableCell = PendingEnterTableCell(
            tableId = prev.id,
            rowId = lastRow.rowId,
            cellId = targetCell.cellId,
            caretAtEnd = true
        )
        return true
    }
    if (prev is TextBlock) {
        clearTableCellFocus()
        val state = getBlockState(prev.id)
        val tfv = state.textFieldValue
        state.restoreTextFieldValue(
            TextFieldValue(
                annotatedString = tfv.annotatedString,
                selection = TextRange(tfv.text.length)
            )
        )
        setTextCursor(prev.id, requestFocus = true)
        return true
    }
    return false
}

/**
 * Tab 在表格末单元格(最后一行最后一列)按下时的处理:
 *
 * - 下方相邻 block 是 [TableBlock]:直接跳到该表格的首行第 0 列 cell
 * - 否则:在当前表格末尾**自动新增一行**,并把光标落到新行第 0 列 cell 末尾
 *
 * 这样既不打断当前表格的连续编辑节奏,又能跨表格连续 Tab 推进。
 *
 * @return true 表示已处理,事件应被消费
 */
internal fun BlockState.tabExitOrAppendRow(tableId: String): Boolean {
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val table = _blocks[index] as? TableBlock ?: return false
    val nextIndex = index + 1
    if (nextIndex < _blocks.size && _blocks[nextIndex] is TableBlock) {
        clearTableCellFocus()
        return requestEnterTableFromBelow((_blocks[nextIndex] as TableBlock).id, preferredColumn = 0)
    }
    // 自动新增一行:复用 insertTableRow,在末行下方插入
    val lastRowIndex = table.rows.lastIndex
    if (lastRowIndex < 0) return false
    insertTableRow(tableId, afterRowIndex = lastRowIndex, insertAbove = false)
    val newTable = _blocks.firstOrNull { it.id == tableId } as? TableBlock ?: return false
    val newRow = newTable.rows.lastOrNull() ?: return false
    val targetCell = newRow.cells.firstOrNull() ?: return false
    pendingEnterTableCell = PendingEnterTableCell(
        tableId = tableId,
        rowId = newRow.rowId,
        cellId = targetCell.cellId,
        caretAtEnd = true
    )
    return true
}

/**
 * Shift+Tab 在表格首单元格 (0, 0) 按下时的处理:
 *
 * - 上方相邻 block 是 [TableBlock]:跳到上方表格的末行末列 cell(光标 cell 末尾)
 * - 上方相邻 block 是 [TextBlock]:光标落到 TextBlock 末尾
 * - 表格已是文档首块:返回 false 不消费(让外层 focus system 默认处理)
 *
 * 注意 Shift+Tab 在 (0,0) 之外位置 → 由调用方走"上一格 cell"逻辑,本函数不处理。
 */
internal fun BlockState.shiftTabExitTable(tableId: String): Boolean {
    val index = findBlockIndex(tableId)
    if (index <= 0) return false
    val prev = _blocks[index - 1]
    if (prev is TableBlock) {
        clearTableCellFocus()
        val lastRow = prev.rows.lastOrNull() ?: return false
        val targetCell = lastRow.cells.lastOrNull() ?: return false
        pendingEnterTableCell = PendingEnterTableCell(
            tableId = prev.id,
            rowId = lastRow.rowId,
            cellId = targetCell.cellId,
            caretAtEnd = true
        )
        return true
    }
    if (prev is TextBlock) {
        clearTableCellFocus()
        val state = getBlockState(prev.id)
        val tfv = state.textFieldValue
        state.restoreTextFieldValue(
            TextFieldValue(
                annotatedString = tfv.annotatedString,
                selection = TextRange(tfv.text.length)
            )
        )
        setTextCursor(prev.id, requestFocus = true)
        return true
    }
    return false
}
